import { useEffect, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import { useParams } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { View } from 'foundations-components/transitional/components/View';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { ACRedirect } from 'academy-common-lib/components/ACRedirect';
import { unwrapChirpError } from 'academy-common-lib/utils/DataFetchingClient';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import getLang from 'academy-common-lib/utils/getLang';
import ApiError from '../../components/Errors/ApiError';
import CourseNavigator from '../../components/Navigator/CourseNavigator';
import TrackDeprecatedDescription from '../../components/TrackCard/TrackDeprecatedDescription';
import LearningContentInnerWrapper from '../../features/learningContent/components/LearningContentInnerWrapper.migrated';
import UsageTracker from '../../utils/UsageTracker';
import LearningPageHeader from './LearningPageHeader';
import LearningPageMainSection from './LearningPageMainSection.migrated';
import LearningPageSidebar from './LearningPageSidebar.migrated';
import TrackBody from './TrackBody';
import { TRACK_INTRO_URL_FRAGMENT } from '../../constants/Navigation.legacy';
import { FILTER_TYPES } from 'academy-common-lib/constants/TopFilterConstants';
import { SCREENS } from '../../constants/Tracking.migrated';
import { maybeLessonCardModelWithProgress } from '../../features/lessons/lessonUtils';
import { PLAYLIST_SEARCH_PARAM } from 'academy-common-lib/features/playlistContent/playlistContentConsts';
import { useSetLastClickedRecommendationItemFromUrl } from '../../features/recommendations/recommendationsHooks.migrated';
import { useFullPlaylist } from '../../features/savedItems/savedItemsQueries.migrated';
import { useTrackRedirection } from '../../features/tracks/tracksRouting';
import { useFullChirpTrack } from '../../features/tracks/tracksQueries';
import { isQuizAsset, isVideoAsset, maybeChirpTrackCardModelWithProgress } from '../../features/tracks/tracksUtils';
import { usePlaylistProgress, useTrackProgressById } from '../../features/userProgress';
import { usePageTitle } from '../../hooks/usePageTitle.legacy';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from 'academy-common-lib/hooks/searchParamHooks';
import { useSingleLanguageRedirect } from '../../hooks/useSingleLangaugeRedirect';
import { isTrackDeprecationWarning } from '../../utils/utils.migrated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTrackPageRouter = () => {
  var _fullLesson$content;
  const {
    trackId: trackIdParam,
    lessonId: lessonIdParam,
    contentId: contentIdParam
  } = useParams();
  const [[playlistIdParam]] = useSearchParam(PLAYLIST_SEARCH_PARAM);
  const trackId = Number(trackIdParam);
  const lessonId = lessonIdParam ? Number(lessonIdParam) : undefined;
  const contentId = contentIdParam ? Number(contentIdParam) : undefined;
  const playlistId = Number(playlistIdParam);
  const lessonFragment = isNaN(Number(lessonIdParam)) ? lessonIdParam : undefined;
  const contentFragment = isNaN(Number(contentIdParam)) ? contentIdParam : undefined;
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const language = searchParamLanguage || (searchParamLanguage || getLang()).toLowerCase();
  const {
    track: fullTrack,
    trackLoading,
    trackError
  } = useFullChirpTrack(trackId);
  const fullLesson = fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.lessons.find(currentLesson => currentLesson.id === lessonId);
  const content = fullLesson === null || fullLesson === void 0 || (_fullLesson$content = fullLesson.content) === null || _fullLesson$content === void 0 ? void 0 : _fullLesson$content.find(currentContent => currentContent.id === contentId);
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(trackId);
  const lessonProgress = trackProgressById === null || trackProgressById === void 0 ? void 0 : trackProgressById.progress.find(p => p.lessonId === Number(lessonId));
  const contentProgress = lessonProgress === null || lessonProgress === void 0 ? void 0 : lessonProgress.contentProgress.find(p => p.contentId === contentId);
  const trackCardModel = maybeChirpTrackCardModelWithProgress(fullTrack, trackProgressById);
  const lessonCardModel = maybeLessonCardModelWithProgress({
    lesson: fullLesson,
    progress: lessonProgress
  });
  const {
    playlist
  } = useFullPlaylist(playlistId, !playlistId);
  const {
    playlistProgress
  } = usePlaylistProgress(playlistId, !playlistId);
  const isTrackIntro = lessonFragment === TRACK_INTRO_URL_FRAGMENT;
  return {
    trackId,
    lessonId,
    contentId,
    language,
    fullTrack,
    trackProgressById,
    trackLoading,
    trackError,
    trackProgressByIdLoading,
    trackProgressByIdError,
    lessonFragment,
    contentFragment,
    isTrackIntro,
    fullLesson,
    lessonProgress,
    trackCardModel,
    lessonCardModel,
    content,
    contentProgress,
    playlistId,
    playlist,
    playlistProgress
  };
};
const TrackPageRouter = () => {
  const {
    trackId,
    lessonId,
    contentId,
    language,
    fullTrack,
    trackProgressById,
    trackLoading,
    trackError,
    lessonFragment,
    contentFragment,
    isTrackIntro,
    fullLesson,
    content,
    trackProgressByIdLoading,
    trackCardModel,
    lessonCardModel,
    contentProgress,
    lessonProgress,
    playlist,
    playlistProgress
  } = useTrackPageRouter();
  const {
    isTablet
  } = useMediaQuery();
  const {
    deprecated = false,
    deprecationWarningDate,
    deprecationFinalDate,
    deprecationMessage,
    deprecationCta
  } = fullTrack || {};
  const {
    redirectUrl: singleLanguageRedirectUrl
  } = useSingleLanguageRedirect({
    contentId: trackId,
    language: language.toUpperCase(),
    contentType: 'COURSE'
  });
  const showWarning = isTrackDeprecationWarning(deprecated, deprecationFinalDate, deprecationWarningDate);
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.trackName',
    options: {
      trackName: fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.name
    },
    skip: !(fullTrack !== null && fullTrack !== void 0 && fullTrack.name)
  });
  useSetLastClickedRecommendationItemFromUrl({
    id: trackId,
    type: 'TRACK'
  });
  const trackAggregatedLessonContents = useMemo(() => fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.lessons.map(currentLesson => currentLesson.content), [fullTrack]);
  const instructors = useMemo(() => {
    if (!trackAggregatedLessonContents) return [];
    const instructorsById = {};
    trackAggregatedLessonContents.forEach(currentLesson => {
      currentLesson.forEach(asset => {
        const isVideo = isVideoAsset(asset);
        if (isVideo && asset.professors) {
          asset.professors.forEach(professor => {
            instructorsById[professor.id] = professor;
          });
        }
      });
    });
    return Object.values(instructorsById);
  }, [trackAggregatedLessonContents]);
  const instructorIds = instructors.map(instructor => instructor.id);
  const ratingsAdditionalTrackingData = fullTrack && {
    contentLanguage: fullTrack.language,
    contentName: fullTrack.name,
    creatorHubId: fullTrack.hubId,
    hsSoftwareFilters: fullTrack.hsSoftwareFilters,
    professorId: instructorIds,
    subCategory: fullTrack.subCategories,
    useCases: fullTrack.useCases
  };
  const assetProgress = useMemo(() => {
    if (!trackProgressById || !trackProgressById.progress) {
      return {};
    }
    const progressMap = {};
    trackProgressById.progress.forEach(currentLesson => {
      if (currentLesson.contentProgress && Array.isArray(currentLesson.contentProgress)) {
        currentLesson.contentProgress.forEach(p => {
          progressMap[p.contentId] = p.progressType;
        });
      }
    });
    if (trackProgressById.introVideoProgress) {
      progressMap[trackProgressById.introVideoProgress.contentId] = trackProgressById.introVideoProgress.progressType;
    }
    return progressMap;
  }, [trackProgressById]);
  const navigator = fullTrack && /*#__PURE__*/_jsx(CourseNavigator, {
    activeContentId: contentId,
    activeLessonId: lessonId || 0,
    assetProgress: assetProgress,
    isTrackIntro: isTrackIntro,
    lessonFragment: lessonFragment,
    track: fullTrack,
    trackProgress: trackProgressById || undefined
  });
  useEffect(() => {
    if (!fullTrack || !fullLesson || !content) return;
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.TRACK,
      subscreen: content.name,
      contentId,
      lessonId,
      trackId,
      trackName: fullTrack.name,
      lessonName: fullLesson.name,
      hsSoftwareFilters: fullTrack.hsSoftwareFilters.join(', '),
      allCategories: fullTrack.subCategories.join(', ')
    });
  }, [contentId, lessonId, trackId, fullTrack, fullLesson, content]);
  useTrackRedirection({
    trackId,
    language: language,
    track: fullTrack,
    lesson: fullLesson,
    contentId,
    lessonId,
    trackProgress: trackProgressById,
    contentFragment,
    lessonFragment
  });
  if (singleLanguageRedirectUrl) {
    return /*#__PURE__*/_jsx(ACRedirect, {
      to: {
        pathname: singleLanguageRedirectUrl,
        search: window.location.search
      }
    });
  }

  // Check for course redirectUrl - redirect to another URL if course is unpublished/deleted
  if (fullTrack !== null && fullTrack !== void 0 && fullTrack.redirectUrl) {
    return /*#__PURE__*/_jsx(ACRedirect, {
      to: {
        pathname: fullTrack.redirectUrl,
        search: window.location.search
      }
    });
  }
  return /*#__PURE__*/_jsx(LearningContentInnerWrapper, {
    backgroundColor: "fill-surface-recessed",
    children: /*#__PURE__*/_jsxs("div", {
      children: [showWarning && !!deprecationMessage && /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "400",
        children: /*#__PURE__*/_jsx(Alert, {
          "data-test-id": "track-deprecated-warning-alert",
          marginBlock: "400",
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.tracks.deprecations.deprecationWarnTitle"
          }),
          type: "info",
          children: /*#__PURE__*/_jsx(TrackDeprecatedDescription, {
            compressed: true,
            cta: deprecationCta,
            message: deprecationMessage,
            trackId: trackId
          })
        })
      }), trackCardModel && /*#__PURE__*/_jsx(View, {
        paddingBlock: "600",
        children: /*#__PURE__*/_jsx(LearningPageHeader, {
          ContentName: (content === null || content === void 0 ? void 0 : content.name) || content && isQuizAsset(content) && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.lessonNavigator.quiz"
          }),
          additionalTrackingData: ratingsAdditionalTrackingData,
          availableLanguages: fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.availableLanguages,
          defaultBackLink: {
            pathname: '/'
          },
          item: lessonCardModel || trackCardModel,
          languageLinkedContent: (fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.languageLinkedContent) || undefined,
          parentTrack: trackCardModel,
          playlist: playlist || undefined,
          playlistProgress: playlistProgress || undefined,
          studyGuide: (fullTrack === null || fullTrack === void 0 ? void 0 : fullTrack.studyGuide) || undefined
        })
      }), trackLoading || trackProgressByIdLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
        "data-test-id": "track-page-container-loader",
        grow: true,
        minHeight: 400
      }) : trackError ? /*#__PURE__*/_jsx(ApiError, {
        contentType: "COURSE",
        error: unwrapChirpError(trackError),
        marker404: "COURSE_VIEW_API_404",
        markerDefault: "COURSE_VIEW_API_ERROR"
      }) : /*#__PURE__*/_jsxs(Flex, {
        alignItems: "stretch",
        flexDirection: isTablet ? 'column-reverse' : 'row',
        gap: "600",
        children: [isTablet ? /*#__PURE__*/_jsx(View, {
          backgroundColor: "fill-surface-default",
          borderColor: "border-core-subtle",
          borderRadius: "300",
          borderStyle: "solid",
          borderWidth: "100",
          overflow: "hidden",
          children: navigator
        }) : /*#__PURE__*/_jsx(LearningPageSidebar, {
          backgroundColor: "fill-surface-default",
          id: "sidebarNavigator",
          withSubNav: 1,
          children: navigator
        }), /*#__PURE__*/_jsx(LearningPageMainSection, {
          viewProps: {
            minInlineSize: '0'
          },
          children: fullTrack ? /*#__PURE__*/_jsx(TrackBody, {
            content: content,
            contentFragment: contentFragment,
            contentProgress: contentProgress,
            lesson: fullLesson,
            lessonFragment: lessonFragment,
            lessonProgress: lessonProgress,
            track: fullTrack,
            trackProgress: trackProgressById || undefined
          }) : /*#__PURE__*/_jsx(NavMarker, {
            name: "COURSE_VIEW_CONTENT_MISSING_ERROR",
            children: /*#__PURE__*/_jsx(ACMissingContentError, {})
          })
        })]
      })]
    })
  });
};
export default TrackPageRouter;